import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Grid,
  Divider,
} from '@mui/material';
import { WorkSpaceIcon, SortAscIcon } from 'components/SVG';
import { useDialog, usePopover } from 'hooks/common';
import React from 'react';
import { SparkImageLoader } from 'sharedComponents/UI';
import { Workspace } from 'types/Workspace';
import { DropdownOption } from 'types/common/DropdownOption';
import AddWorkspaceDialog from './Modals/AddWorkspaceDialog';
import WorkspaceSkeleton from 'sharedComponents/Skeleton/WorkspaceSkeleton ';

type WorkspaceMenuProps = {
  workspaceOptions: DropdownOption[];
  handleWorkspaceSelect: (workspaceId: string) => void;
  workSpace: Workspace | undefined;
  isProduction: boolean;
  minimumView?: boolean;
};

const WorkspaceMenu = ({
  workspaceOptions,
  handleWorkspaceSelect,
  workSpace,
  isProduction,
  minimumView = false,
}: WorkspaceMenuProps) => {
  const [opacity, setOpacity] = React.useState<number>(0);
  const { anchorEl, openPopover, open, closePopover } = usePopover();

  const {
    open: isCreateWorkspaceModalOpen,
    openDialog: openCreateWorkspaceModal,
    closeDialog: closeCreateWorkspaceModal,
  } = useDialog();

  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const handleSortIconClick = (event: React.MouseEvent<HTMLElement>) => {
    if (isCreateWorkspaceModalOpen) {
      return;
    }
    setIsLoading(true);
    openPopover(event);

    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  };
  return (
    <Box
      display='flex'
      justifyContent='center'
      alignItems='center'
      component='div'
      onPointerOver={() => setOpacity(0.8)}
      onPointerLeave={() => !open && setOpacity(0)}
      onClick={(event) => handleSortIconClick(event)}
      sx={{ cursor: 'pointer' }}
    >
      <Box sx={{ display: 'flex', ml: 2 }}>
        {workSpace?.attributes.imageURL ? (
          <SparkImageLoader
            src={workSpace?.attributes.imageURL}
            alt={workSpace?.attributes.imageURL}
            style={{
              width: minimumView ? '30px' : '40px',
              height: minimumView ? '30px' : '40px',
              borderRadius: '50%',
            }}
          />
        ) : (
          <WorkSpaceIcon width='40' height='40' />
        )}
      </Box>
      {!minimumView && (
        <Typography
          variant='h6'
          noWrap
          component='a'
          sx={{
            ml: 2,
            color: '#000',
            fontSize: '16px',
            fontStyle: 'normal',
            lineHeight: 'normal',
          }}
        >
          {workSpace?.attributes.name}
        </Typography>
      )}
      <IconButton
        disableFocusRipple
        disableTouchRipple
        disableRipple
        sx={{
          opacity: minimumView ? 1 : opacity,
          transition: 'opacity 0.3s',
        }}
      >
        <SortAscIcon />
      </IconButton>

      <Menu
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        onClose={closePopover}
      >
        {isLoading ? (
          <WorkspaceSkeleton />
        ) : (
          workspaceOptions.map((option) => (
            <MenuItem
              key={option.value}
              onClick={() => handleWorkspaceSelect(option.value as string)}
            >
              <Grid container alignItems='center'>
                {option.iconUrl ? (
                  <SparkImageLoader
                    src={option.iconUrl}
                    alt={option.iconUrl}
                    loading='lazy'
                    style={{
                      width: '30px',
                      height: '30px',
                      borderRadius: '10px',
                    }}
                  />
                ) : (
                  <WorkSpaceIcon width='30' height='30' />
                )}
                <Typography
                  sx={{
                    fontSize: '18px',
                    ml: 2,
                    color: '#000000',
                  }}
                >
                  {option.label}
                </Typography>
              </Grid>
            </MenuItem>
          ))
        )}

        {!isProduction && (
          <>
            <Box p={2}>
              <Divider />
            </Box>
            <MenuItem
              onClick={() => {
                openCreateWorkspaceModal();
                closePopover();
              }}
              sx={{ display: 'flex', gap: 2, alignItems: 'center' }}
            >
              <Box
                width={30}
                height={30}
                bgcolor={(theme) => theme.palette.primary.main}
                borderRadius={'50%'}
                display={'flex'}
                justifyContent={'center'}
                alignItems={'center'}
              >
                <Typography
                  variant='h5'
                  fontSize={30}
                  color='white'
                  marginTop={0.6}
                >
                  +
                </Typography>
              </Box>
              <Typography
                variant='body2'
                sx={{ fontFamily: 'Mont-SemiBold', fontSize: '16px' }}
              >
                Add Workspace
              </Typography>
            </MenuItem>
          </>
        )}
      </Menu>

      {isCreateWorkspaceModalOpen && (
        <AddWorkspaceDialog
          open={isCreateWorkspaceModalOpen}
          onClose={closeCreateWorkspaceModal}
        />
      )}
    </Box>
  );
};

export default WorkspaceMenu;
